// 负责 storefront 顶层状态编排，与 UI 组件和 domain 纯函数关联。
import { useMemo, useState } from "react";
import {
  createServerCheckoutSession,
  getCheckoutMode,
  resolveServerMockPayment
} from "./checkoutApi";
import { products, type Product, type ProductCategory } from "../domain/products";
import {
  buildCartLines,
  calculateSubtotal,
  type CartItem
} from "../domain/cart";
import {
  applyPaymentEvent,
  createOrder,
  type CustomerDetails,
  type MockPaymentOutcome,
  type Order
} from "../domain/orders";
import {
  createMockPaymentEvent,
  createMockPaymentSession
} from "../domain/payments";

export type SortMode = "Featured" | "Price: Low" | "Price: High";
export type StoreView = "catalog" | "mock-checkout" | "order-result";

export const categories: Array<ProductCategory | "All"> = [
  "All",
  "Dresses",
  "Tops",
  "Outerwear",
  "Sets"
];

// 根据分类、搜索词和排序生成展示商品列表。
const filterProducts = (
  selectedCategory: ProductCategory | "All",
  query: string,
  sortMode: SortMode
): Product[] => {
  const normalizedQuery = query.trim().toLowerCase();
  const filtered = products.filter((product) => {
    const matchesCategory =
      selectedCategory === "All" || product.category === selectedCategory;
    const searchable = `${product.name} ${product.category} ${product.color} ${product.tags.join(
      " "
    )}`.toLowerCase();

    return matchesCategory && searchable.includes(normalizedQuery);
  });

  return [...filtered].sort((left, right) => {
    if (sortMode === "Price: Low") {
      return left.price - right.price;
    }

    if (sortMode === "Price: High") {
      return right.price - left.price;
    }

    return 0;
  });
};

// 生成浏览器侧 Mock 订单号，服务端落库后应替换为服务端 ID。
const createBrowserOrderId = (): string => {
  return `MOCK-${crypto.randomUUID().slice(0, 8).toUpperCase()}`;
};

// 生成浏览器侧 Mock 支付事件 ID，后续会由服务端支付事件表负责。
const createBrowserPaymentEventId = (): string => {
  return `evt_${crypto.randomUUID()}`;
};

// 提供当前时间，便于后续服务端替换为统一时间源。
const now = (): string => new Date().toISOString();
const checkoutMode = getCheckoutMode();

export const useStorefront = () => {
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory | "All">(
    "All"
  );
  const [query, setQuery] = useState("");
  const [sortMode, setSortMode] = useState<SortMode>("Featured");
  const [selectedProduct, setSelectedProduct] = useState<Product>(products[0]);
  const [selectedSize, setSelectedSize] = useState(products[0].sizes[1]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [view, setView] = useState<StoreView>("catalog");
  const [activeOrder, setActiveOrder] = useState<Order | null>(null);
  const [customer, setCustomer] = useState<CustomerDetails>({
    email: "",
    country: "United States"
  });
  const [checkoutError, setCheckoutError] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [isPaymentResolving, setIsPaymentResolving] = useState(false);

  const visibleProducts = useMemo(
    () => filterProducts(selectedCategory, query, sortMode),
    [selectedCategory, query, sortMode]
  );

  const cartLines = useMemo(
    () => buildCartLines(cartItems, products),
    [cartItems]
  );
  const subtotal = calculateSubtotal(cartLines);
  const itemCount = cartItems.reduce((total, item) => total + item.quantity, 0);
  const estimatedShipping = subtotal >= 90 || subtotal === 0 ? 0 : 9;
  const checkoutTotal = subtotal + estimatedShipping;
  const canCheckout = cartItems.length > 0;

  // 选择商品并重置尺码到该商品第一个可选尺码。
  const selectProduct = (product: Product) => {
    setSelectedProduct(product);
    setSelectedSize(product.sizes[0]);
  };

  // 将当前详情商品按尺码加入购物车。
  const addSelectedProduct = () => {
    setCartItems((currentItems) => {
      const existingItem = currentItems.find(
        (item) =>
          item.productId === selectedProduct.id && item.size === selectedSize
      );

      if (existingItem) {
        return currentItems.map((item) =>
          item.productId === selectedProduct.id && item.size === selectedSize
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }

      return [
        ...currentItems,
        {
          productId: selectedProduct.id,
          size: selectedSize,
          quantity: 1
        }
      ];
    });
    setIsCartOpen(true);
  };

  // 修改购物车数量，数量降到 0 时移除该行。
  const updateQuantity = (productId: string, size: string, change: number) => {
    setCartItems((currentItems) =>
      currentItems
        .map((item) =>
          item.productId === productId && item.size === size
            ? { ...item, quantity: item.quantity + change }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  // 创建 Mock 支付会话，并进入本地模拟结账页。
  const startMockCheckout = async () => {
    setCheckoutError("");
    setPaymentError("");
    setIsCheckoutLoading(true);

    try {
      const order =
        checkoutMode === "server"
          ? await createServerCheckoutSession(cartItems, customer)
          : createOrder({
              lines: cartLines,
              customer,
              shipping: estimatedShipping,
              createId: createBrowserOrderId,
              now
            });
      const session = createMockPaymentSession(order);

      setActiveOrder(order);
      setIsCartOpen(false);
      setView(session.redirectView);
    } catch (error) {
      setCheckoutError(error instanceof Error ? error.message : "Checkout failed.");
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  // 应用 Mock 支付结果，模拟后续真实 webhook 更新订单状态。
  const resolveMockPayment = async (outcome: MockPaymentOutcome) => {
    if (!activeOrder) {
      throw new Error("Active order is required before resolving mock payment.");
    }

    setPaymentError("");
    setIsPaymentResolving(true);

    try {
      const updatedOrder =
        checkoutMode === "server"
          ? await resolveServerMockPayment(activeOrder.id, outcome)
          : applyPaymentEvent(
              activeOrder,
              createMockPaymentEvent({
                orderId: activeOrder.id,
                outcome,
                createId: createBrowserPaymentEventId,
                now
              })
            );

      setActiveOrder(updatedOrder);
      setView("order-result");

      if (updatedOrder.paymentStatus === "paid") {
        setCartItems([]);
      }
    } catch (error) {
      setPaymentError(
        error instanceof Error ? error.message : "Payment resolution failed."
      );
    } finally {
      setIsPaymentResolving(false);
    }
  };

  // 返回购物车，保留当前订单草稿以便继续模拟支付。
  const backToBag = () => {
    setPaymentError("");
    setView("catalog");
    setIsCartOpen(true);
  };

  // 完成结果页查看后回到商品目录。
  const continueShopping = () => {
    setPaymentError("");
    setCheckoutError("");
    setView("catalog");
    setActiveOrder(null);
  };

  return {
    activeOrder,
    addSelectedProduct,
    backToBag,
    canCheckout,
    cartLines,
    checkoutError,
    checkoutTotal,
    customer,
    estimatedShipping,
    isCheckoutLoading,
    isCartOpen,
    isPaymentResolving,
    itemCount,
    paymentError,
    query,
    resolveMockPayment,
    selectedCategory,
    selectedProduct,
    selectedSize,
    selectProduct,
    setCustomer,
    setIsCartOpen,
    setQuery,
    setSelectedCategory,
    setSelectedSize,
    setSortMode,
    sortMode,
    startMockCheckout,
    subtotal,
    updateQuantity,
    view,
    visibleProducts,
    continueShopping
  };
};
